import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { coreAdminClient } from 'app/api/apiClient/generated/coreAdminClient';
import { ListCompanyResponseItem } from 'app/api/apiClient/generated/types';
import Box from 'app/components/v2/components/Box';
import TypeAhead, { TypeAheadOption } from 'app/components/v2/components/TypeAhead';
import { useTypeAhead } from 'app/hooks';

import { useInternalUserCompanyJumper } from './hooks';

// Component Definition
const InternalUserCompanyJumper: FC = () => {
  const { t } = useTranslation('components');
  const [isFetching, setIsFetching] = useState(false);
  const [isJumping, setIsJumping] = useState(false);
  const companyField = useTypeAhead('',
    null);
  const [companies, setCompanies] = useState<ListCompanyResponseItem[]>([]);
  const internalUserCompanyJumper = useInternalUserCompanyJumper();

  async function handleOptionSelected(value: TypeAheadOption) {
    // This function gets called multiple times when the option changes.
    // The first call has a null value, and then a subsequent call with the selected value.
    // Only jump when a value is actually selected otherwise the jump fails.
    companyField.onTypeAheadSelect(value);
    if (value.id && !isJumping) {
      setIsJumping(true);
      await internalUserCompanyJumper.jumpToCompany({
        companyId: Number(value.id),
      });
    }
  }

  async function handleUpdateOptions(q: string) {
    setIsFetching(true);
    try {
      const response = await coreAdminClient.listCompanies({
        companyTypeId: undefined,
        includeInactive: undefined,
        page: undefined,
        pageSize: undefined,
        q,
        sortAsc: undefined,
        sortField: undefined,
      });
      setCompanies(response.data?.companies ?? []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetching(false);
    }
  }

  const companyOptions = useMemo(() => companies.map<TypeAheadOption>(c => ({
    id: c.id,
    label: c.name ?? '',
  })).filter(option => !!option.label),
  [companies]);

  return (
    <Box
      alignItems="flex-end"
      display="flex"
      flexDirection="column"
      marginBottom={6}
    >
      <TypeAhead
        {...companyField}
        autoFocus
        debounce
        disabled={isJumping}
        isFetching={isFetching || isJumping}
        label={t('companyJumper.findCompanyNameLabel', 'Enter company name')}
        onTypeAheadSelect={handleOptionSelected}
        onUpdateOptions={handleUpdateOptions}
        options={companyOptions}
      />
    </Box>
  );
};

export default InternalUserCompanyJumper;
